import React, { useEffect, useState } from 'react'
import { DataGrid } from '@mui/x-data-grid'
import { Paper, Snackbar, Alert } from '@mui/material'
import { HiOutlineEye } from 'react-icons/hi'

import DeviceDetailsModal from './DeviceDetailsModal'
import TermAndConditionModal from './TermAndConditionModal'
import { accepted, fetchInvetory } from '../../services/InventoryService'

const DEFAULT_PAGE_SIZE = 10
const SEARCH_DEBOUNCE = 400

const STATUS_STYLES = {
    ASSIGNED: 'bg-green-100 text-green-700',
    PENDING: 'bg-yellow-100 text-yellow-700',
    RETURNED: 'bg-red-100 text-red-700'
}

const InventoryList = () => {
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)

    const [searchInput, setSearchInput] = useState('')
    const [searchTerm, setSearchTerm] = useState('')

    const [paginationModel, setPaginationModel] = useState({
        page: 0, // zero-based
        pageSize: DEFAULT_PAGE_SIZE
    })

    const [openDeviceModal, setOpenDeviceModal] = useState(false)
    const [openAcceptanceModal, setAcceptanceModal] = useState(false)
    const [selectedDevice, setSelectedDevice] = useState(null)

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    })

    // Debounced search
    useEffect(() => {
        const timeout = setTimeout(() => {
            setSearchTerm(searchInput)
            setPaginationModel((prev) => ({ ...prev, page: 0 })) // reset to first page
        }, SEARCH_DEBOUNCE)

        return () => clearTimeout(timeout)
    }, [searchInput])

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)
            try {
                const response = await fetchInvetory({
                    page: paginationModel.page + 1, // API is 1-based
                    limit: paginationModel.pageSize,
                    search: searchTerm
                })

                const data = response?.data || []
                setTotalCount(response?.count || 0)

                setRows(
                    data.map((record, index) => ({
                        id: `${record.id}-${paginationModel.page}-${index}`, // ensure unique ID
                        name: `${record.employee.firstName.toUpperCase()} ${record.employee.lastName.toUpperCase()}`,
                        item: `${record.item.category.toUpperCase()} - ${record.item.brand.toUpperCase()} ${record.item.model.toUpperCase()}`,
                        serialNumber: record.item.serialNumber || 'N/A',
                        assignedDate: record.assignedDate
                            ? new Intl.DateTimeFormat('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: '2-digit'
                              }).format(new Date(record.assignedDate))
                            : '—',
                        status: record.status.toUpperCase()
                    }))
                )
            } catch (err) {
                console.error(err)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [paginationModel, searchTerm]) // 🔑 dependencies

    // Accept terms
    const handleAcceptTerms = async (deviceId) => {
        try {
            const response = await accepted({ token: deviceId })

            setSnackbar({
                open: true,
                message: response.message || 'Terms accepted successfully.',
                severity: 'success'
            })

            // refresh current page
            setPaginationModel((prev) => ({ ...prev }))
        } catch {
            setSnackbar({
                open: true,
                message: 'Failed to accept terms.',
                severity: 'error'
            })
        } finally {
            setAcceptanceModal(false)
        }
    }

    const columns = [
        { field: 'name', headerName: 'Name', flex: 1 },
        { field: 'item', headerName: 'Item', flex: 1 },
        {
            field: 'serialNumber',
            headerName: 'Serial No.',
            flex: 1,
            renderCell: (params) =>
                params.value === 'N/A' ? <span className="text-gray-400 italic">{params.value}</span> : params.value
        },
        { field: 'assignedDate', headerName: 'Assigned Date', flex: 1 },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => (
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[params.value] || ''}`}>
                    {params.value}
                </span>
            )
        },
        {
            field: 'actions',
            headerName: 'Actions',
            sortable: false,
            flex: 1,
            renderCell: (params) => (
                <button
                    className="text-blue-600 hover:text-blue-800"
                    onClick={(e) => {
                        e.stopPropagation()
                        setSelectedDevice(params.row)
                        setOpenDeviceModal(true)
                    }}
                >
                    <HiOutlineEye className="w-6 h-6" />
                </button>
            )
        }
    ]

    return (
        <div className="flex flex-col p-4">
            <h1 className="text-xl font-bold mb-4">Employee Device Assignments</h1>

            <input
                type="text"
                placeholder="Search by item, serial number, or employee"
                className="p-2 border rounded mb-3"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
            />

            <p className="text-sm text-gray-500 mb-2">
                Showing {rows.length} of {totalCount} assignments
            </p>

            <Paper sx={{ width: '100%', height: 'calc(100vh - 280px)' }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    paginationMode="server"
                    rowCount={totalCount}
                    pageSizeOptions={[5, 10, 25]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(model) => setPaginationModel(model)}
                />
            </Paper>

            <DeviceDetailsModal
                open={openDeviceModal}
                onClose={() => setOpenDeviceModal(false)}
                device={selectedDevice}
            />

            <TermAndConditionModal
                open={openAcceptanceModal}
                onClose={() => setAcceptanceModal(false)}
                device={selectedDevice}
                onAcceptTerms={handleAcceptTerms}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            >
                <Alert severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    )
}

export default InventoryList
